import { getHeaderToIndexMap, getUsedRangeWithProperties, getRowsWithProperties } from "../utils/excel";
import { ExcelValidationError, NoSheetExistsError } from "../utils/ExcelValidationError";
import { Atin } from "./sthAtinParser";
import {
  BLANK_BASELINE_TAXABILITY,
  FIRST_JURISDICTION,
  LAST_JURISDICTION,
  NEW_PTC_NAME,
  PTC_NAME_COLUMN,
  TDM_REQUIRED_COLUMNS,
} from "../utils/constants";

export interface Ptc {
  readonly ptcName: string;
  readonly baselineTaxability: string;
  readonly isNew: boolean;
  readonly atinsMapped: Atin[];
  readonly isUsedForSth?: boolean;
  taxabilityPatternNum?: number; // Acts like a hash for the taxability pattern string
}

export const parsePtcs = (worksheetName: string, setLoadingText: (loadingText: string) => void) => {
  return Excel.run(async (context) => {
    setLoadingText("Reading PTCs from TDM...");

    const ptcBaselineTaxabilitySheet = context.workbook.worksheets.getItemOrNullObject(worksheetName);
    await context.sync();

    if (ptcBaselineTaxabilitySheet.isNullObject) {
      throw NoSheetExistsError(worksheetName);
    }

    const usedRange = await getUsedRangeWithProperties(context, ptcBaselineTaxabilitySheet, [
      "rowCount",
      "columnCount",
    ]);

    const headerToIndex = await getHeaderToIndexMap(usedRange, context, worksheetName);
    for (const column of TDM_REQUIRED_COLUMNS) {
      if (!headerToIndex.has(column)) {
        throw new ExcelValidationError(`Missing column: ${column}`, worksheetName);
      }
    }

    const ptcNameToPtc = new Map<string, Ptc>();
    const rows = await getRowsWithProperties(usedRange, context, ["values"]);
    for (let i = 1; i < rows.length; i++) {
      const row = rows[i];

      const ptcName = row.values[0][headerToIndex.get(PTC_NAME_COLUMN)].toString().trim();

      if (ptcName.startsWith(NEW_PTC_NAME)) {
        throw new ExcelValidationError("Please rename New PTC", worksheetName, i + 1);
      }

      let baselineTaxability = "";
      for (let j = headerToIndex.get(FIRST_JURISDICTION); j <= headerToIndex.get(LAST_JURISDICTION); j++) {
        const taxability = row.values[0][j].toString().trim();
        baselineTaxability += taxability ? taxability : BLANK_BASELINE_TAXABILITY;
      }

      ptcNameToPtc.set(ptcName, {
        ptcName,
        atinsMapped: [],
        isNew: false,
        baselineTaxability,
      });
      row.untrack();
    }
    assignTaxabilityPatternValues(Array.from(ptcNameToPtc.values()));
    return ptcNameToPtc;
  });
};

// Assign a different integer to every unique taxability pattern we find
const assignTaxabilityPatternValues = (ptcs: Ptc[]) => {
  const taxabilityPatternStringToNum = new Map<string, number>();
  let currentNum = 1;
  ptcs.forEach((ptc) => {
    if (!taxabilityPatternStringToNum.has(ptc.baselineTaxability)) {
      taxabilityPatternStringToNum.set(ptc.baselineTaxability, currentNum);
      currentNum++;
    }
    ptc.taxabilityPatternNum = taxabilityPatternStringToNum.get(ptc.baselineTaxability);
  });
};
